import type { PaginatedData } from '~/domains/services/interfaces/paginated-data.interface';
import { isApiError } from '~/domains/services/utils/isApiError';
import { objectToTuples } from '~/services/utils';

import type { SurvivorProp } from '../gamecenter/domains/playerSurvivor/types/survivorProp';

import type { PropsSettings } from './interfaces/props-settings.interface';
import type {
  APIAlternateInjuredErrorResponse,
  APIAlternateRequiredErrorResponse,
  APIGetBoostsResponse,
  APIGetGroupSlipResponse,
  APIGetGrouSplipEntriesResponse,
  APIGetLeaderboardSlipsResponse,
  APIPropExpanded,
  APIGetPropsByIdsResponse,
  APIGetPropsResponse,
  APIGetV1PopularResponse,
  APIGetSlipResponse,
  APIGetSlipsResponse,
  APIValidateSlipErrorResponse,
  APITooManyInjuredPlayersErrorResponse,
  CreateSlipErrorResponse,
  CreateSlipPayload,
  CreateSlipSuccessResponse,
  GetPropsFiltersResponse,
  GetPropsPayload,
  GetQuickPicksPayoutsPayload,
  GetQuickPicksPayoutsResponse,
  LocationAllowed,
  SlipStatus,
  ValidateSlipPayload,
} from './interfaces/propsService/api';
import type { APIQuickPicksDiscoverResponse } from './interfaces/propsService/api/discover';
import type {
  APIValidateBoostResponse,
  ValidateBoostPayload,
} from './interfaces/propsService/api/boosts';
import type {
  TGetPlayerCardResponse,
  TGetPropsByIdsResponse,
  TGetPropsResponse,
} from './interfaces/propsService/parsed/props';
import type {
  GetSlipsResponse,
  TGetGroupSlipResponse,
  TGetSlipResponse,
  TValidateSlipErrorResponse,
  TValidateSlipResponse,
} from './interfaces/propsService/parsed/slips';
import parsePropExpanded from './parsers/parsePropExpanded';
import parsePropsResponse from './parsers/parsePropsResponse';
import parsePropsResponseV1 from './parsers/parsePropsResponseV1';
import { parseGroupSlipResponse } from './parsers/parseSlipGroupResponse';
import { parseSlipResponse } from './parsers/parseSlipResponse';
import { parseSlips } from './parsers/parseSlips';
import parseValidateErrorResponse from './parsers/parseValidateErrorResponse';
import { propsServiceInstance } from './props.service.instance';

const getDiscover = async (params: {
  disallowed?: string[];
}): Promise<APIQuickPicksDiscoverResponse> => {
  const query = new URLSearchParams(objectToTuples(params)).toString();
  const response = await propsServiceInstance.get<APIQuickPicksDiscoverResponse>(
    `/v1/discover?${query}`
  );
  return response.data;
};

const getFilters = async (disallowed: string[]): Promise<GetPropsFiltersResponse> => {
  const query = new URLSearchParams(objectToTuples({ disallowed })).toString();
  const response = await propsServiceInstance.get<GetPropsFiltersResponse>(`/filters?${query}`);
  return response.data;
};

const getProps = async (payload: GetPropsPayload): Promise<TGetPropsResponse> => {
  const query = new URLSearchParams(objectToTuples(payload)).toString();
  const response = await propsServiceInstance.get<APIGetPropsResponse>(`/props?${query}`);
  return parsePropsResponse(response.data);
};

function enrichApiPropWithPopularIncluded(
  prop: APIPropExpanded,
  included?: APIGetV1PopularResponse['included']
): APIPropExpanded {
  if (!included) {
    return prop;
  }

  const gamesMap = new Map((included.games ?? []).map((g) => [g.id, g]));
  const entitiesMap = new Map((included.entities ?? []).map((e) => [e.id, e]));
  const teamsMap = new Map((included.teams ?? []).map((t) => [t.id, t]));

  const game = prop.game_id ? gamesMap.get(prop.game_id) : undefined;
  const entity = prop.entity_id ? entitiesMap.get(prop.entity_id) : undefined;
  const team = prop.team_id ? teamsMap.get(prop.team_id) : undefined;

  return {
    ...prop,
    ...(game ? { game } : {}),
    ...(entity ? { entity } : {}),
    ...(team ? { team } : {}),
  };
}

const getPopularProps = async (payload: {
  limit: number;
  offset: number;
  disallowed?: string[];
}): Promise<TGetPropsResponse> => {
  const query = new URLSearchParams(objectToTuples(payload)).toString();
  const response = await propsServiceInstance.get<APIGetV1PopularResponse>(`/v1/popular?${query}`);
  const body = response.data;
  return {
    status: body.status,
    total: body.total,
    offset: body.offset,
    limit: body.limit,
    data: body.data.map((prop) =>
      parsePropExpanded(enrichApiPropWithPopularIncluded(prop, body.included))
    ),
  };
};

const getPropsV1 = async (payload: GetPropsPayload): Promise<TGetPropsResponse> => {
  const query = new URLSearchParams(objectToTuples(payload)).toString();
  const response = await propsServiceInstance.get<APIGetPropsResponse>(`/v1/props?${query}`);
  return parsePropsResponseV1(response.data);
};

const getPlayerCardData = async (
  id: string,
  disallowed?: string[]
): Promise<TGetPlayerCardResponse> => {
  const query = new URLSearchParams(objectToTuples({ disallowed })).toString();
  const response = await propsServiceInstance.get<TGetPlayerCardResponse>(`/props/${id}?${query}`);
  return response.data;
};

const getSettings = async (): Promise<PropsSettings> => {
  const response = await propsServiceInstance.get<PropsSettings>(`/settings`);
  return response.data;
};

/** @deprecated Use /settings with optional token instead */
const getLocationAllowed = async (): Promise<LocationAllowed> => {
  const response = await propsServiceInstance.get<LocationAllowed>(`/location_allowed`);
  return response.data;
};

/** @deprecated Use postSlip() instead */
const submitBetslip = async (): Promise<PaginatedData<PropsSettings>> => {
  const response = await propsServiceInstance.post<PaginatedData<PropsSettings>>(`/slips`);
  return response.data;
};

const getSlips = async ({
  limit,
  offset,
  include,
  parentId,
}: {
  limit: number;
  offset: number;
  include: SlipStatus[];
  parentId?: string;
}): Promise<GetSlipsResponse> => {
  const query = new URLSearchParams(
    objectToTuples({
      include,
      limit,
      offset,
      parent_id: parentId,
    })
  ).toString();
  const response = await propsServiceInstance.get<APIGetSlipsResponse>(`/slips?${query}`);
  return parseSlips(response.data);
};

const getSlip = async (slipId: string): Promise<TGetSlipResponse> => {
  const response = await propsServiceInstance.get<APIGetSlipResponse>(`/slips/${slipId}`);

  return parseSlipResponse(response.data);
};

const getGroupSlip = async (groupId: string, slipId: string): Promise<TGetGroupSlipResponse> => {
  const response = await propsServiceInstance.get<APIGetGroupSlipResponse>(
    `/groups/${groupId}/slips/${slipId}`
  );

  return parseGroupSlipResponse(response.data);
};

const cancelSlip = async (slipId: string): Promise<string> => {
  await propsServiceInstance.delete(`/slips/${slipId}`);
  return slipId;
};

const getGroupSplitEntries = async ({
  groupId,
  limit,
  offset,
}: {
  limit: number;
  offset: number;
  groupId: string;
}): Promise<APIGetGrouSplipEntriesResponse> => {
  const query = new URLSearchParams(
    objectToTuples({
      limit,
      offset,
    })
  ).toString();

  const response = await propsServiceInstance.get<APIGetGrouSplipEntriesResponse>(
    `/groups/${groupId}/entries?${query}`
  );
  return response.data;
};

const getGroupLeaderboard = async ({
  groupId,
  limit,
  offset,
  myEntries,
}: {
  limit: number;
  offset: number;
  groupId: string;
  myEntries?: boolean;
}): Promise<APIGetLeaderboardSlipsResponse> => {
  const query = new URLSearchParams(
    objectToTuples({
      limit,
      offset,
      ...(myEntries && { my_entries: true }),
      sort: 'potential_points',
    })
  ).toString();

  const response = await propsServiceInstance.get<APIGetLeaderboardSlipsResponse>(
    `/groups/${groupId}/slips?${query}`
  );
  return response.data;
};

const postSlip = async (
  payload: CreateSlipPayload
): Promise<
  | CreateSlipSuccessResponse
  | TValidateSlipErrorResponse
  | CreateSlipErrorResponse
  | APITooManyInjuredPlayersErrorResponse
  | APIAlternateRequiredErrorResponse
  | APIAlternateInjuredErrorResponse
> => {
  try {
    const response = await propsServiceInstance.post(`/slips`, payload);

    return response.data;
  } catch (e) {
    if (isApiError(e) && (e.response?.status === 422 || e.response?.status === 400)) {
      const errorData = e.response?.data as
        | APIValidateSlipErrorResponse
        | CreateSlipErrorResponse
        | APITooManyInjuredPlayersErrorResponse
        | APIAlternateRequiredErrorResponse
        | APIAlternateInjuredErrorResponse;

      if ((errorData as APITooManyInjuredPlayersErrorResponse)?.error === 'TOO_MANY_INJURED_PLAYERS') {
        return errorData as APITooManyInjuredPlayersErrorResponse;
      }

      if ((errorData as APIAlternateRequiredErrorResponse)?.error === 'ALTERNATE_REQUIRED') {
        return errorData as APIAlternateRequiredErrorResponse;
      }

      if ((errorData as APIAlternateInjuredErrorResponse)?.error === 'ALTERNATE_INJURED') {
        return errorData as APIAlternateInjuredErrorResponse;
      }

      if (
        (errorData as APIValidateSlipErrorResponse)?.error === 'INVALID_PROPS' ||
        (errorData as APIValidateSlipErrorResponse)?.error === 'PROP_DISABLED' ||
        (errorData as APIValidateSlipErrorResponse)?.error === 'GAME_NOT_IN_SLATE'
      ) {
        return parseValidateErrorResponse(errorData as APIValidateSlipErrorResponse);
      }

      if ((errorData as CreateSlipErrorResponse)?.error === 'MULTIPLIER_CHANGED') {
        return errorData as CreateSlipErrorResponse;
      }
    }

    throw e;
  }
};

const postValidateSlip = async (payload: ValidateSlipPayload): Promise<TValidateSlipResponse> => {
  try {
    const response = await propsServiceInstance.post(`/slips/validate`, payload);

    return response.data;
  } catch (e) {
    if (isApiError(e)) {
      const errorData = e.response?.data as { error?: string };
      if (errorData?.error === 'TOO_MANY_INJURED_PLAYERS') {
        return e.response?.data as APITooManyInjuredPlayersErrorResponse;
      }

      if (errorData?.error === 'ALTERNATE_REQUIRED') {
        return e.response?.data as APIAlternateRequiredErrorResponse;
      }

      if (errorData?.error === 'ALTERNATE_INJURED') {
        return e.response?.data as APIAlternateInjuredErrorResponse;
      }
    }

    if (
      isApiError(e) &&
      e.response?.status === 422 &&
      ((e.response?.data as APIValidateSlipErrorResponse)?.error === 'INVALID_PROPS' ||
        (e.response?.data as APIValidateSlipErrorResponse)?.error === 'PROP_DISABLED' ||
        (e.response?.data as APIValidateSlipErrorResponse)?.error === 'GAME_NOT_IN_SLATE')
    ) {
      return parseValidateErrorResponse(e.response?.data as APIValidateSlipErrorResponse);
    }

    throw e;
  }
};

const getBoosts = async ({
  limit,
  offset,
}: {
  limit: number;
  offset: number;
}): Promise<APIGetBoostsResponse> => {
  const query = new URLSearchParams(
    objectToTuples({
      limit,
      offset,
    })
  ).toString();
  const response = await propsServiceInstance.get(`/boosts?${query}`);

  return response.data;
};

const validateBoost = async ({
  boostId,
  payload,
}: ValidateBoostPayload): Promise<APIValidateBoostResponse> => {
  const response = await propsServiceInstance.post(`/boosts/${boostId}/validate`, payload);

  return response.data;
};

const getPropsByIds = async (
  ids: string,
  disallowed: string[]
): Promise<TGetPropsByIdsResponse> => {
  const query = new URLSearchParams(
    objectToTuples({
      disallowed,
      ids,
    })
  ).toString();
  const { data } = await propsServiceInstance.get<APIGetPropsByIdsResponse>(
    `/props/by_ids?${query}`
  );
  return {
    data: data.data.map(parsePropExpanded),
    status: data.status,
  };
};

const getSurvivorPropById = async (propId: string): Promise<{ prop: SurvivorProp }> => {
  const response = await propsServiceInstance.get(`/survivor-props/${propId}`);

  return response.data;
};

const getQuickPicksPayouts = async (
  payload: GetQuickPicksPayoutsPayload
): Promise<GetQuickPicksPayoutsResponse> => {
  const response = await propsServiceInstance.post<GetQuickPicksPayoutsResponse>(
    `/slips/p2p/payouts`,
    payload
  );
  return response.data;
};

export {
  cancelSlip,
  getBoosts,
  getDiscover,
  getFilters,
  getGroupLeaderboard,
  getPopularProps,
  getGroupSlip,
  getGroupSplitEntries,
  getLocationAllowed,
  getPlayerCardData,
  getProps,
  getPropsV1,
  getPropsByIds,
  getQuickPicksPayouts,
  getSettings,
  getSlip,
  getSlips,
  postSlip,
  postValidateSlip,
  submitBetslip,
  validateBoost,
  getSurvivorPropById,
};
